import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { fetchInfracoes } from '@/services/anatel';
import type { InfracaoFilters } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * Infraction detail and PAYMENT status.
 *
 * Every money figure elsewhere in this product is a fine IMPOSED. This registry
 * is the only place in any of the three corpora that records what happened to
 * the money afterwards — settled, in collection, on an instalment plan.
 *
 * Three traps come with it and all three are on the face of the screen:
 * the process-total column repeats on every infraction row so the obvious sum
 * is many times too large; payment status is per infraction, so a process can
 * be part settled and part in collection; and the registry reaches 92.2% of the
 * analysed corpus through a legacy process number.
 */
export function AnatelInfracoesPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [filters, setFilters] = useState<InfracaoFilters>({ limit: PAGE_SIZE, offset: 0 });
  const [draft, setDraft] = useState('');

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['anatel', 'infracoes', filters],
    queryFn: () => fetchInfracoes(filters),
    placeholderData: keepPreviousData,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const offset = filters.offset ?? 0;
  const update = (next: Partial<InfracaoFilters>) =>
    setFilters((current) => ({ ...current, ...next, offset: 0 }));

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const statuses = data.payment_mix.value?.statuses ?? [];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:infracoes.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:infracoes.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:infracoes.explainer" />

      <FieldGuide i18nKey="anatel:infracoes.guide" level={2} />

      <div className="flex justify-end">
        {/* The exported rows carry no process-total column, exactly as the table
            below does not. The two honest totals ride in the file's preamble. */}
        <ExportButton path="/api/v1/anatel/export/infracoes" body={filters} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      {/* The bridge, stated before any figure. The registry is six times wider
          than the analysed corpus, so "128,598 infractions" is not this
          product's population and must not read as if it were. */}
      <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
        {t('anatel:infracoes.bridge', {
          bridged: number.format(data.corpus.bridged_processes),
          analysed: number.format(data.corpus.analysed_processes),
          rows: number.format(data.corpus.bridged_rows),
          registryRows: number.format(data.corpus.registry_rows),
          registryProcesses: number.format(data.corpus.registry_processes),
        })}
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={{ ...data.payment_mix, value: statuses }}
          titleKey="anatel:infracoes.payment.title"
          helpKey="anatel:infracoes.payment.help"
          labelPrefix="anatel:infracoes.status."
        />

        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('anatel:infracoes.money.title')}
          </h2>
          <Answer answer={data.money} help={t('anatel:infracoes.money.help')}>
            {(money) => (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Figure
                    label={t('anatel:infracoes.money.perInfraction')}
                    value={currency.format(money.per_infraction_total)}
                  />
                  <Figure
                    label={t('anatel:infracoes.money.perProcess')}
                    value={currency.format(money.deduplicated_process_total)}
                  />
                </div>

                {/* The trap, shown rather than merely warned about. A reader who
                    runs the obvious query gets this number, and seeing it beside
                    the honest one is what makes the caveat land. */}
                <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    {t('anatel:infracoes.money.naiveWarning', {
                      naive: currency.format(money.naive_process_total),
                      factor: new Intl.NumberFormat(i18n.language, {
                        maximumFractionDigits: 1,
                      }).format(money.inflation_if_naive),
                    })}
                  </span>
                </p>
              </div>
            )}
          </Answer>
        </section>
      </div>

      <OutcomeMixPanel
        answer={data.economic_groups}
        titleKey="anatel:infracoes.groups.title"
        helpKey="anatel:infracoes.groups.help"
        labelPrefix="anatel:infracoes.group."
      />

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          update({ query: draft.trim() || null });
        }}
      >
        <div className="min-w-[14rem] flex-1 space-y-1">
          <label htmlFor="infracao-query" className="text-xs font-medium text-foreground">
            {t('anatel:infracoes.filters.search')}
          </label>
          <input
            id="infracao-query"
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('anatel:infracoes.filters.searchPlaceholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="infracao-status" className="block text-xs font-medium text-foreground">
            {t('anatel:infracoes.filters.status')}
          </label>
          <select
            id="infracao-status"
            value={filters.payment_status ?? ''}
            onChange={(event) => update({ payment_status: event.target.value || null })}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('anatel:infracoes.filters.statusAny')}</option>
            {statuses.map((status) => (
              <option key={status.code} value={status.code}>
                {t(`anatel:infracoes.status.${status.code}`, { defaultValue: status.code })}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background"
        >
          {t('anatel:infracoes.filters.apply')}
        </button>

        <span className="ml-auto text-xs text-muted-foreground" aria-live="polite">
          {t('anatel:infracoes.showing', { total: number.format(data.total) })}
          {isFetching ? ` · ${t('common:state.loading')}` : ''}
        </span>
      </form>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t('anatel:infracoes.columns.process')}</th>
              <th className="px-3 py-2 font-medium">{t('anatel:infracoes.columns.norma')}</th>
              <th className="px-3 py-2 font-medium">{t('anatel:infracoes.columns.group')}</th>
              <th className="px-3 py-2 font-medium">{t('anatel:infracoes.columns.payment')}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t('anatel:infracoes.columns.fine')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item, index) => (
              <tr key={`${item.process_key}-${index}`} className="border-t hover:bg-muted/30">
                <td className="px-3 py-2">
                  {/* Always linkable: the bridge IS the join, so every row here
                      belongs to a process this product analyses. */}
                  <Link
                    to={`/anatel/p/${item.process_key}`}
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    {item.process_number}
                  </Link>
                </td>
                <td className="max-w-xs truncate px-3 py-2 text-muted-foreground" title={item.norma ?? ''}>
                  {item.norma ?? '—'}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{item.grupo_economico ?? '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {t(`anatel:infracoes.status.${item.situacao_pagamento ?? '-'}`, {
                    defaultValue: item.situacao_pagamento ?? '—',
                  })}
                </td>
                {/* The per-INFRACTION amount. The process total is deliberately
                    not a column: a reader would sum it down the page and get the
                    inflated figure this screen exists to warn about. */}
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-foreground">
                  {item.multa_por_infracao_brl === null
                    ? ''
                    : currency.format(item.multa_por_infracao_brl)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => setFilters({ ...filters, offset: Math.max(offset - PAGE_SIZE, 0) })}
          className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {t('anatel:decisions.previous')}
        </button>
        <button
          type="button"
          disabled={offset + PAGE_SIZE >= data.total}
          onClick={() => setFilters({ ...filters, offset: offset + PAGE_SIZE })}
          className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {t('anatel:decisions.next')}
        </button>
      </div>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">{value}</p>
    </div>
  );
}
